import type { ReactNode } from 'react';
import { Building2, CreditCard, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import type { PayabliRail } from '@/lib/payments/payabli';
import { cn } from '@/lib/utils';

/**
 * The customer-facing Card | Bank account choice. One control for both
 * processors: Payabli mounts its form for the chosen rail, and Stripe opens a
 * Checkout session for it, so the payer sees the same choice either way.
 */
export function PaymentRailSwitch({
  value,
  onChange,
  disabled,
  achSavings,
  testIdPrefix,
}: {
  value: PayabliRail;
  onChange: (rail: PayabliRail) => void;
  disabled?: boolean;
  /** Shown under "Bank account", e.g. "Save 3% with ACH". */
  achSavings?: ReactNode;
  /** `<prefix>-switch`, `<prefix>-card` and `<prefix>-ach`. */
  testIdPrefix: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Payment method"
      className="grid grid-cols-2 gap-1 rounded-xl bg-stone-100 p-1 dark:bg-[#121110]"
      data-testid={`${testIdPrefix}-switch`}
    >
      {(
        [
          { rail: 'card', label: 'Card', Icon: CreditCard },
          { rail: 'ach', label: 'Bank account', Icon: Building2 },
        ] as const
      ).map(({ rail, label, Icon }) => (
        <button
          key={rail}
          type="button"
          role="radio"
          aria-checked={value === rail}
          disabled={disabled}
          onClick={() => {
            if (rail !== value) onChange(rail);
          }}
          data-testid={`${testIdPrefix}-${rail}`}
          className={cn(
            'flex min-h-11 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-[15px] font-medium transition-colors focus-visible:ring-2 focus-visible:ring-stone-500 focus-visible:outline-none disabled:opacity-60',
            value === rail
              ? 'border-stone-200 bg-white text-stone-900 shadow-sm dark:border-[#3a3835] dark:bg-[#1f1d1b] dark:text-stone-50'
              : 'border-transparent text-stone-500 hover:text-stone-900 dark:text-stone-400 dark:hover:text-stone-100',
          )}
        >
          <span className="flex min-w-0 flex-col items-center gap-1.5">
            <span className="flex items-center gap-2">
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </span>
            {rail === 'ach' ? achSavings : null}
          </span>
        </button>
      ))}
    </div>
  );
}

/** The one reassurance line under a customer pay button. */
export function SecureNote({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-center justify-center gap-2 text-sm text-stone-500 dark:text-stone-400">
      <ShieldCheck className="h-4 w-4" aria-hidden />
      {children}
    </p>
  );
}

/** The large pay / save button under the customer form, with its reassurance line. */
export function CustomerPayButton({
  label,
  busy,
  disabled,
  onClick,
  secureNote = 'Secure, encrypted connection',
  className,
  testId,
}: {
  label: ReactNode;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
  secureNote?: string;
  /** Overrides the colours, e.g. the portal's accent. */
  className?: string;
  testId?: string;
}) {
  return (
    <div className="space-y-4">
      <Button
        type="button"
        className={cn(
          'h-12 w-full rounded-xl bg-stone-900 text-base text-white hover:bg-stone-800 dark:bg-[#f2efe9] dark:text-stone-900 dark:hover:bg-white',
          className,
        )}
        disabled={disabled || busy}
        onClick={onClick}
        data-testid={testId}
      >
        {busy ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Lock className="mr-2 h-4 w-4" />
        )}
        {label}
      </Button>
      <SecureNote>{secureNote}</SecureNote>
    </div>
  );
}
